import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { MapPin, CalendarDays, UtensilsCrossed, ListChecks } from "lucide-react";

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function ItineraryDetailDialog({ itinerary, open, onOpenChange }) {
  if (!itinerary) return null;

  const {
    Title,
    Destination,
    TripStartDate,
    TripEndDate,
    Summary,
    ActivityInfo,
    MealInfo,
  } = itinerary;

  const dateRange =
    TripStartDate || TripEndDate
      ? `${TripStartDate ? formatDate(TripStartDate) : "N/A"} – ${TripEndDate ? formatDate(TripEndDate) : "N/A"
      }`
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg">
            {Title || "Itinerary detail"}
          </DialogTitle>
          {(Destination || dateRange) && (
            <DialogDescription className="mt-1 space-y-1 text-xs text-muted-foreground">
              {Destination && (
                <div className="inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3 text-primary" />
                  <span>{Destination}</span>
                </div>
              )}
              {dateRange && (
                <div className="flex items-center gap-1 text-xs">
                  <CalendarDays className="h-3 w-3 text-primary" />
                  <span>{dateRange}</span>
                </div>
              )}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="mt-2 space-y-3 text-sm">
          {Summary && (
            <section className="space-y-1">
              <h3 className="text-xs font-semibold text-foreground">Overview</h3>
              <p className="text-xs text-muted-foreground whitespace-pre-line">
                {Summary}
              </p>
            </section>
          )}

          {ActivityInfo && (
            <section className="space-y-1">
              <div className="flex items-center gap-1 text-xs font-semibold">
                <ListChecks className="h-3 w-3 text-primary" />
                <span>Activities</span>
              </div>
              <p className="text-xs text-muted-foreground whitespace-pre-line">
                {ActivityInfo}
              </p>
            </section>
          )}

          {MealInfo && (
            <section className="space-y-1">
              <div className="flex items-center gap-1 text-xs font-semibold">
                <UtensilsCrossed className="h-3 w-3 text-primary" />
                <span>Meals</span>
              </div>
              <p className="text-xs text-muted-foreground whitespace-pre-line">
                {MealInfo}
              </p>
            </section>
          )}

          {!Summary && !ActivityInfo && !MealInfo && (
            <p className="text-xs text-muted-foreground">
              Detailed information for this itinerary will be provided by your
              travel consultant.
            </p>
          )}

          <div className="pt-2">
            <Badge variant="outline" className="text-[11px]">
              Day itinerary
            </Badge>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ItineraryDetailDialog;
